import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, X, Edit3 } from "lucide-react";
import api from "@/lib/api";

const empty = {
  name: "",
  slug: "",
  description: "",
  hero_image: "",
  order: 0,
  status: "active",
  coming_soon: false,
};

export default function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} for new, obj for edit

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Catalog</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Categories</h1>
          <p className="mt-2 text-sm text-emerald-100/60">{rows.length} categor{rows.length === 1 ? "y" : "ies"}.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} data-testid="admin-cat-new" className="btn-edge text-sm">
          <Plus size={14} /> New category
        </button>
      </div>

      <div className="card-edge overflow-hidden">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
            <tr>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Slug</th>
              <th className="px-5 py-3 font-medium">Order</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-emerald-100/50">No categories yet.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-emerald-500/8 last:border-0 hover:bg-emerald-500/[0.03]">
                <td className="px-5 py-3.5 text-white">{c.name}</td>
                <td className="px-5 py-3.5 font-mono-edge text-xs text-emerald-100/60">{c.slug}</td>
                <td className="px-5 py-3.5 text-emerald-100/70">{c.order}</td>
                <td className="px-5 py-3.5">
                  <span className={`chip ${c.coming_soon ? "chip-amber" : "chip-emerald"}`}>
                    {c.coming_soon ? "coming soon" : c.status}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing(c)} data-testid={`admin-cat-edit-${c.id}`}
                      className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50 hover:text-white">
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => remove(c)} data-testid={`admin-cat-delete-${c.id}`}
                      className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-red-500/40 hover:text-red-300">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && <Modal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Modal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        description: form.description || "",
        hero_image: form.hero_image || null,
        order: Number(form.order) || 0,
        status: form.status || "active",
        coming_soon: !!form.coming_soon,
      };
      if (isNew) {
        await api.post("/admin/categories", payload);
        toast.success("Category created");
      } else {
        await api.put(`/admin/categories/${item.id}`, payload);
        toast.success("Category updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-emerald-500/12 p-5">
          <div>
            <p className="overline">{isNew ? "New" : "Edit"} category</p>
            <h3 className="mt-1 font-editorial text-xl text-white">{isNew ? "Add category" : form.name}</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="grid gap-4 overflow-y-auto p-5">
          <Field label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} required testId="cat-name" />
          <Field label="Slug (optional)" v={form.slug} on={(v) => setForm({ ...form, slug: v })} placeholder="auto from name" testId="cat-slug" />
          <Field label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} textarea testId="cat-desc" />
          <Field label="Hero image URL" v={form.hero_image} on={(v) => setForm({ ...form, hero_image: v })} testId="cat-img" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Order" v={form.order} type="number" on={(v) => setForm({ ...form, order: v })} testId="cat-order" />
            <div>
              <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2.5 text-sm text-white">
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-emerald-100/80">
            <input type="checkbox" checked={form.coming_soon} onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })} />
            Coming soon (public flag)
          </label>
          <button disabled={saving} data-testid="cat-save" className="btn-edge justify-center">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, v, on, textarea, type = "text", required, placeholder, testId }) {
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      {textarea ? (
        <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={3} data-testid={testId}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" placeholder={placeholder} />
      ) : (
        <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} required={required} data-testid={testId}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" placeholder={placeholder} />
      )}
    </div>
  );
}
